import { Box, Slider, Stack, Typography } from '@mui/material';
import NumberInput from '~/components/Inputs/NumberInput';

const SLIDER_MIN_CM = -5;
const SLIDER_MAX_CM = 5;
const SLIDER_STEP_CM = 0.1;

// Tick at 0 cm marks the source structure surface (no label — avoids
// floating text in the layout)
const SLIDER_MARKS = [{ value: 0 }];

const inputStyle = {
    textAlign: 'center' as const,
    width: '50px',
    fontSize: 13,
    paddingTop: 5,
    paddingBottom: 4,
};

interface BoundaryRowProps {
    label: string;
    value: number;
    onChange: (value: number) => void;
    min: number;
    max: number;
}

function BoundaryRow({ label, value, onChange, min, max }: BoundaryRowProps) {
    return (
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={0.5}
        >
            <Typography fontSize={12} color="text.secondary">
                {label}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
                <NumberInput
                    value={value}
                    onChange={(v) => onChange(v ?? 0)}
                    min={min}
                    max={max}
                    step={SLIDER_STEP_CM}
                    precision={1}
                    inputProps={{ style: inputStyle }}
                    tooltipPlacement="left"
                />
                <Typography fontSize={12} color="text.secondary">
                    cm
                </Typography>
            </Stack>
        </Stack>
    );
}

export interface RingRangeControlsProps {
    innerCm: number;
    outerCm: number;
    onInnerChange: (value: number) => void;
    onOuterChange: (value: number) => void;
    onSliderChange: (_: Event, value: number | number[]) => void;
}

export default function RingRangeControls({
    innerCm,
    outerCm,
    onInnerChange,
    onOuterChange,
    onSliderChange,
}: RingRangeControlsProps) {
    return (
        <Box>
            <BoundaryRow
                label="Inner boundary"
                value={innerCm}
                onChange={onInnerChange}
                min={SLIDER_MIN_CM}
                max={outerCm - SLIDER_STEP_CM}
            />
            <BoundaryRow
                label="Outer boundary"
                value={outerCm}
                onChange={onOuterChange}
                min={innerCm + SLIDER_STEP_CM}
                max={SLIDER_MAX_CM}
            />
            {/* Range slider — tick at 0 marks the source structure surface.
                disableSwap prevents thumbs from crossing, enforcing
                outer > inner at the interaction level. */}
            <Slider
                value={[innerCm, outerCm]}
                onChange={onSliderChange}
                min={SLIDER_MIN_CM}
                max={SLIDER_MAX_CM}
                step={SLIDER_STEP_CM}
                marks={SLIDER_MARKS}
                disableSwap
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v.toFixed(1)} cm`}
                sx={{
                    '& .MuiSlider-mark': {
                        width: 2,
                        height: 12,
                        borderRadius: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.35)',
                    },
                    '& .MuiSlider-markActive': {
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        opacity: 1,
                    },
                }}
            />
        </Box>
    );
}
